import { hasValidSavedTracePairs, isSafeSavedTraceKey, hasUniqueSavedTraceKeys } from '@kangaroo/shared';
import type { SavedTraceView, SavedTraceViewDefinition, SavedTraceViewSummary } from '@kangaroo/shared';
export type TraceViewDefinition = Omit<SavedTraceViewDefinition, 'schemaVersion'>;
export type LegacyTraceView = TraceViewDefinition & { id: string };
export type CloudTraceView = SavedTraceView;
export type TraceViewSummary = SavedTraceViewSummary;
export const LEGACY_TRACE_VIEWS_KEY = 'paddiq.saved-trace-views.v1';
export function traceViewScope(user: { id: string; teamId?: string | null } | null) {
  return user ? JSON.stringify([user.id, user.teamId ?? user.id]) : '';
}
export function traceViewBrowserStorage(): Storage | undefined {
  try { return typeof window === 'undefined' ? undefined : window.localStorage; } catch { return undefined; }
}
export function readLegacyTraceViews(storage: Pick<Storage, 'getItem'> | undefined): LegacyTraceView[] {
  try {
    const entries: unknown = JSON.parse(storage?.getItem(LEGACY_TRACE_VIEWS_KEY) ?? '[]');
    if (!Array.isArray(entries)) return [];
    return entries.filter((entry): entry is LegacyTraceView => (
      entry && typeof entry.id === 'string' && typeof entry.name === 'string'
      && Array.isArray(entry.traceKeys) && entry.traceKeys.every(isTraceKey) && hasUniqueSavedTraceKeys(entry.traceKeys)
      && ['labels', 'pairs', 'units'].every((field) => validMap(entry[field], field === 'labels' ? 120 : 80))
      && hasValidSavedTracePairs(entry.traceKeys, entry.pairs)
    ));
  } catch { return []; }
}
export function migrationAcknowledgmentKey(scope: string, legacyId: string) {
  return `paddiq.trace-view-import.v1:${encodeURIComponent(scope)}:${encodeURIComponent(legacyId)}`;
}
export type TraceViewsState = {
  items: TraceViewSummary[]; nextCursor: string | null; total: number;
  busy: boolean; error: string | null; conflict: boolean;
};
export type TraceViewsRequest = <T>(method: 'GET' | 'PUT' | 'POST' | 'DELETE', path: string,
  body: unknown, signal: AbortSignal, expectedScope: string) => Promise<T>;

type TraceViewsPage = { items: TraceViewSummary[]; nextCursor: string | null; total: number };
class InvalidTraceViewResponse extends Error {}
const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const isText = (value: unknown, maximum: number): value is string => typeof value === 'string' && value.length <= maximum;
const isTraceKey = isSafeSavedTraceKey;
const isCount = (value: unknown, maximum = Number.MAX_SAFE_INTEGER): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 && value <= maximum;
const isViewId = (value: unknown): value is string => typeof value === 'string' && /^[a-zA-Z0-9_-]{1,96}$/.test(value);
function isMetadata(value: unknown): value is Record<string, unknown> {
  return isRecord(value) && isViewId(value.id) && value.schemaVersion === 1
    && isText(value.name, 120) && value.name.trim().length > 0
    && isCount(value.revision) && value.revision > 0
    && typeof value.createdAt === 'string' && Number.isFinite(Date.parse(value.createdAt))
    && typeof value.updatedAt === 'string' && Number.isFinite(Date.parse(value.updatedAt));
}
function validMap(value: unknown, valueLimit: number) {
  return value === undefined || (isRecord(value) && Object.keys(value).length <= 256
    && Object.entries(value).every(([key, label]) => isTraceKey(key) && isText(label, valueLimit)));
}
function parseView(value: unknown, expectedId?: string): CloudTraceView {
  if (!isMetadata(value) || (expectedId !== undefined && value.id !== expectedId)
    || !Array.isArray(value.traceKeys) || value.traceKeys.length > 256
    || !value.traceKeys.every(isTraceKey) || !hasUniqueSavedTraceKeys(value.traceKeys)
    || !validMap(value.labels, 120) || !validMap(value.pairs, 80) || !validMap(value.units, 80)
    || !hasValidSavedTracePairs(value.traceKeys, value.pairs as Record<string, string> | undefined)) {
    throw new InvalidTraceViewResponse();
  }
  return value as unknown as CloudTraceView;
}
function parsePage(value: unknown): TraceViewsPage {
  if (!isRecord(value) || !Array.isArray(value.items) || value.items.length > 50
    || !value.items.every((item) => isMetadata(item) && isCount(item.traceCount, 256))
    || !isCount(value.total) || value.total < value.items.length
    || !(value.nextCursor === null || (isText(value.nextCursor, 512) && value.nextCursor.length > 0))) {
    throw new InvalidTraceViewResponse();
  }
  return value as TraceViewsPage;
}
export function traceViewSelectionStorageKey(scope: string) {
  return `paddiq.trace-view-selection.v1:${encodeURIComponent(scope)}`;
}
export function readSelectedCloudTraceView(scope: string, storage: Pick<Storage, 'getItem'> | undefined = traceViewBrowserStorage()): string | null {
  if (!scope) return null;
  try {
    const id = storage?.getItem(traceViewSelectionStorageKey(scope));
    return isViewId(id) ? id : null;
  } catch { return null; }
}
export function writeSelectedCloudTraceView(scope: string, id: string | null, storage: Pick<Storage, 'setItem' | 'removeItem'> | undefined = traceViewBrowserStorage()) {
  if (!scope || (id !== null && !isViewId(id))) return;
  try {
    if (id === null) storage?.removeItem(traceViewSelectionStorageKey(scope));
    else storage?.setItem(traceViewSelectionStorageKey(scope), id);
  } catch { /* Preference storage is optional; the server remains authoritative for definitions. */ }
}

/** Scope-fenced requests and explicit imports; no local data is changed or automatically uploaded. */
export class TraceViewsClient {
  state: TraceViewsState = { items: [], nextCursor: null, total: 0, busy: false, error: null, conflict: false };
  private controller = new AbortController();
  private scope = '';
  private generation = 0;
  constructor(private request: TraceViewsRequest, private currentScope: () => string,
    private changed: () => void, private storage?: Pick<Storage, 'getItem' | 'setItem'>) {}
  setScope(scope: string) {
    this.controller.abort();
    this.controller = new AbortController();
    this.generation += 1;
    this.scope = scope;
    this.state = { items: [], nextCursor: null, total: 0, busy: false, error: null, conflict: false };
    this.changed();
  }
  dispose() { this.controller.abort(); this.generation += 1; }
  private async run<T>(operation: (signal: AbortSignal, scope: string) => Promise<T>): Promise<T | undefined> {
    const generation = this.generation;
    const scope = this.scope;
    if (!scope || scope !== this.currentScope() || this.state.busy) return undefined;
    this.state = { ...this.state, busy: true, error: null, conflict: false }; this.changed();
    try {
      const result = await operation(this.controller.signal, scope);
      if (generation !== this.generation || scope !== this.currentScope()) return undefined;
      return result;
    } catch (error) {
      if (generation === this.generation && scope === this.currentScope()) {
        const response = (error as { response?: { status?: number; data?: { code?: string } } })?.response;
        const status = response?.status;
        const scopeChanged = response?.data?.code === 'saved_view_scope_changed';
        this.state = { ...this.state, conflict: !scopeChanged && (status === 409 || status === 410),
          error: error instanceof InvalidTraceViewResponse
            ? 'The server returned an invalid saved-view response. Your draft and browser saves are retained. Refresh saved views or retry the operation.'
            : scopeChanged
            ? 'Your account or workspace changed. Your draft is retained. Refresh your sign-in before saving, then review the destination and confirm ownership again before importing.'
            : status === 409 || status === 410
            ? 'This view changed or was deleted elsewhere. Your draft is retained. Reload the saved version or save your draft as a new view.'
            : status === 404 ? 'This saved view is no longer available. Your draft is retained.'
              : status === 403 ? 'Saved views are unavailable in this account or session. Your draft and browser saves are retained.'
                : status && status >= 400 && status < 500 ? `Saved view request rejected (${status}). Check the name, channel definitions and account limits. Your draft and browser saves are retained.`
                  : 'Could not access saved views. Check your connection and retry. Your draft and browser saves are retained.' };
      }
      return undefined;
    } finally {
      if (generation === this.generation) { this.state = { ...this.state, busy: false }; this.changed(); }
    }
  }
  async list(more = false) {
    const cursor = more ? this.state.nextCursor : null;
    if (more && !cursor) return;
    const page = await this.run(async (signal, scope) => parsePage(await this.request<unknown>(
      'GET', `/trace-views?limit=25${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`, undefined, signal, scope)));
    if (page) {
      this.state = { ...this.state, items: more
        ? [...new Map([...this.state.items, ...page.items].map((item) => [item.id, item])).values()] : page.items,
      nextCursor: page.nextCursor, total: page.total };
      this.changed();
    }
  }
  get(id: string) {
    return this.run(async (signal, scope) => parseView(await this.request<unknown>('GET', `/trace-views/${encodeURIComponent(id)}`, undefined, signal, scope), id));
  }
  save(id: string, definition: TraceViewDefinition, expectedRevision: number | null) {
    return this.run(async (signal, scope) => parseView(await this.request<unknown>('PUT', `/trace-views/${encodeURIComponent(id)}`,
      { ...definition, schemaVersion: 1, expectedRevision }, signal, scope), id));
  }
  remove(id: string, expectedRevision: number) {
    return this.run(async (signal, scope) => {
      const result = await this.request<unknown>('DELETE', `/trace-views/${encodeURIComponent(id)}`, { expectedRevision }, signal, scope);
      if (!isRecord(result) || result.deleted !== true) throw new InvalidTraceViewResponse();
      return { deleted: true as const };
    });
  }
  async importLegacy(view: LegacyTraceView, consentScope: string, confirmed: boolean) {
    if (!confirmed || consentScope !== this.scope || consentScope !== this.currentScope()) return undefined;
    const { id, ...definition } = view;
    const result = await this.run(async (signal, scope) => {
      const response = await this.request<unknown>('POST', '/trace-views/import', { ...definition, schemaVersion: 1, legacyId: id }, signal, scope);
      if (!isRecord(response)) throw new InvalidTraceViewResponse();
      if (response.status === 'deleted' && response.view === null) return { status: 'deleted' as const, view: null };
      if (response.status !== 'imported' && response.status !== 'existing') throw new InvalidTraceViewResponse();
      return { status: response.status, view: parseView(response.view) };
    });
    if (result) {
      try { this.storage?.setItem(migrationAcknowledgmentKey(consentScope, id), result.status); } catch { /* Cloud result remains valid; retry is idempotent. */ }
    }
    return result;
  }
}
/** A retry after a lost response must target the original create-only PUT. */
export class PendingTraceViewCreate {
  private pending: { fingerprint: string; id: string } | null = null;
  constructor(private createId: () => string = () => `trace-view-${crypto.randomUUID()}`) {}
  get id() { return this.pending?.id ?? null; }
  forDraft(definition: TraceViewDefinition) {
    const fingerprint = JSON.stringify(definition);
    if (this.pending?.fingerprint !== fingerprint) this.pending = { fingerprint, id: this.createId() };
    return this.pending.id;
  }
  clear() { this.pending = null; }
}
/** Apply an explicit editor pairing action, detaching either endpoint from its former chart. */
export function pairSavedTraceWithPrevious(traceKeys: readonly string[], pairs: Record<string, string>, key: string, paired: boolean) {
  if (!paired) return Object.fromEntries(Object.entries(pairs).filter(([, secondary]) => secondary !== key));
  const index = traceKeys.indexOf(key);
  const previous = index > 0 ? traceKeys[index - 1] : undefined;
  if (!previous || previous === key) return pairs;
  return {
    ...Object.fromEntries(Object.entries(pairs).filter(([primary, secondary]) =>
      primary !== previous && secondary !== previous && primary !== key && secondary !== key)),
    [previous]: key,
  };
}

/** Removing selected channels also removes their pair edges; unrelated chart definitions survive. */
export function retainSelectedTracePairs(traceKeys: readonly string[], pairs: Record<string, string>) {
  const selected = new Set(traceKeys);
  return Object.fromEntries(Object.entries(pairs).filter(([primary, secondary]) => selected.has(primary) && selected.has(secondary)));
}
